import type { RovoConfiguration } from '@jira/ai-rovo_actions-shared-types/src/RovoConfiguration.types.tsx';
import {
	createActionsHook,
	createSelectorHook,
	type HookActionsFunction,
} from '@atlassian/react-sweet-state/src/components/hook';
import { createStore } from '@atlassian/react-sweet-state/src/store/create';
import type { Action, BoundActions, Store } from '@atlassian/react-sweet-state/src/types';

interface State {
	configuration: RovoConfiguration;
}

const noopConfiguration: RovoConfiguration = {
	getActions: async () => [],
};

const initialState: State = {
	configuration: noopConfiguration,
};

const actions = {
	setConfiguration:
		(configuration: RovoConfiguration): Action<State> =>
		({ setState }) => {
			setState({ configuration });
		},
	resetConfiguration:
		(): Action<State> =>
		({ setState }) => {
			setState({ configuration: noopConfiguration });
		},
	updateConfiguration:
		(configuration: Partial<RovoConfiguration>): Action<State> =>
		({ setState, getState }) => {
			const state = getState();
			setState({ configuration: { ...state.configuration, ...configuration } });
		},
};

export const aiRovoCurrentRouteConfigurationStore: Store<
	State,
	{
		setConfiguration: (configuration: RovoConfiguration) => Action<State>;
		resetConfiguration: () => Action<State>;
		updateConfiguration: (configuration: Partial<RovoConfiguration>) => Action<State>;
	}
> = createStore<State, typeof actions>({
	initialState,
	actions,
});

export const useAiRovoCurrentRouteConfigurationStoreActions: HookActionsFunction<
	BoundActions<
		State,
		{
			setConfiguration: (configuration: RovoConfiguration) => Action<State>;
			resetConfiguration: () => Action<State>;
			updateConfiguration: (configuration: Partial<RovoConfiguration>) => Action<State>;
		}
	>
> = createActionsHook(aiRovoCurrentRouteConfigurationStore);
export const useAiRovoCurrentRouteConfigurationStore: <U>(selector: (state: State) => U) => U =
	createSelectorHook(aiRovoCurrentRouteConfigurationStore);

const selectCurrentRouteNudges = (state: State): RovoConfiguration['nudges'] =>
	state.configuration?.nudges;

export const useAiRovoCurrentRouteNudges: () => RovoConfiguration['nudges'] = () =>
	useAiRovoCurrentRouteConfigurationStore(selectCurrentRouteNudges);
